import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Blogs.css';
import blogService from '../services/blogService';
import { blogPosts as legacyPosts } from '../data/blogData';
import { sanitizeHtmlForRender } from '../utils/sanitizeHtml';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

export function BlogsPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await blogService.getPublishedBlogs();
        if (data && data.length > 0) {
          setBlogs(data);
        } else {
          setBlogs(legacyPosts);
        }
      } catch (error) {
        console.error('Failed to load blogs, falling back to legacy content', error);
        setBlogs(legacyPosts);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const previewText = (blog) => {
    if (blog.excerpt) return blog.excerpt;
    if (blog.content) {
      return blog.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
    }
    return '';
  };

  const handleBlogClick = (blogId) => {
    navigate(`/blog/${blogId}`);
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  return (
    <div className="blogs-page">
      <HelmetProvider>
        <Helmet>
          <title>
            Blogs - Get Fit with Daniella
          </title>

          <meta
            name="description"
            content="Read the latest fitness, nutrition, and lifestyle tips from Daniella."
          />

          <link rel="canonical" href="https://getfitwithdaniella.com/blogs/" />

          {/* Open Graph */}
          <meta
            property="og:title"
            content="Blogs - Get Fit with Daniella"
          />

          <meta
            property="og:description"
            content="Read the latest fitness, nutrition, and lifestyle tips from Daniella."
          />

          <meta
            property="og:url"
            content="https://getfitwithdaniella.com/blogs/"
          />

          <meta property="og:type" content="website" />

          <meta
            property="og:site_name"
            content="Get Fit with Daniella"
          />

          <meta
            property="og:image"
            content="https://getfitwithdaniella.com/assets/og-image.jpg"
          />

          {/* Twitter / X */}
          <meta
            name="twitter:card"
            content="summary_large_image"
          />

          <meta
            name="twitter:title"
            content="Blogs - Get Fit with Daniella"
          />

          <meta
            name="twitter:description"
            content="Read the latest fitness, nutrition, and lifestyle tips from Daniella."
          />

          <meta
            name="twitter:image"
            content="https://www.getfitwithdaniella.com/assets/og-image.jpg"
          />
        </Helmet>
      </HelmetProvider>

      <div className="blogs-page-header">
        <div className="blogs-page-container">
          <h1>Blogs By Daniella</h1>
          <p>Daniella’s Fitness, Nutrition & Lifestyle Blog- Expert-backed guidance on workouts, nutrition, and habits that actually fit your life.</p>
        </div>
      </div>

      <div className="blogs-container">
        {loading ? (
          <div className="blogs-loading">Loading blogs...</div>
        ) : (
          <div className="blogs-grid">
            {blogs.map((blog) => (
              <article 
                key={blog.id} 
                className="blog-card"
                onClick={() => handleBlogClick(blog.id)}
              >
                {blog.image_url ? (
                  <div className="blog-card-image-wrap">
                    <img src={getImageUrl(blog.image_url)} alt={blog.title} className="blog-card-image" loading="lazy" />
                  </div>
                ) : null}
                <div className="blog-card-content">
                  <h3 className="blog-card-title">
                    {/* {blog.title} */}
                    {typeof blog.title === 'string' ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(blog.title) }} />
                    ) : (
                      blog.title
                    )}
                    </h3>
                  <p className="blog-card-excerpt">
                    {/* {previewText(blog)} */}
                    {typeof previewText(blog) === 'string' ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(previewText(blog)) }} />
                    ) : (
                      previewText(blog)
                    )}
                  </p>
                  <div className="blog-card-footer">
                    <span className="blog-card-author">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      {blog.author || 'Daniella'}
                    </span>
                    <span className="blog-card-date">{blog.date || formatDate(blog.published_at)}</span>
                  </div>
                  <button className="read-more-btn">
                    Read More
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
